/* global ResizeObserver, PointerEvent */
// phase-compiler-tab-pins — the pinnable-tab compiler workspace shell.
//
// The MAIN tabs are split into two PANE-ATTACHED headers so each tab sits above
// the pane it controls (the user's explicit correction):
//
//   • the CTD Tree tab + its pin — plus the three compact LAYOUT preset icon
//     buttons, tucked into the spare space beside the tree tab — live in a small
//     header directly above the tree pane (the left, resizable column), and
//   • the other six tabs (Documents, Review, Bookmarks & Links, Validation,
//     Lifecycle, Package) + their pins live in the header above the non-tree
//     pane area (the right side), dedicated to those six so they fit ONE ROW.
//
// The six-tab right strip never wraps: it stays nowrap and, only when the space
// is genuinely insufficient, scrolls horizontally so no tab or pin is hidden or
// clipped. The layout buttons are plain buttons in a role="group" (NOT the
// tablist) so they never join the arrow-key tab cycle.
//
// The ONE shared selected-file header renders ONCE above BOTH pane headers, never
// duplicated. Every tab stays reachable in its header even when its pane is
// hidden — so the tree tab is pickable when the tree is unpinned/not-active, and
// the other tabs are pickable when only the tree is on screen.
//
// EVERY tab carries a PIN affordance (the familiar pushpin: filled when pinned,
// outline when not) adjacent to the tab button, so it never joins the arrow-key
// tablist cycle. A pinned tab stays VISIBLE while a different tab is active: the
// panes on screen are the UNION of the active tab and the pinned tabs. Pin
// toggling is independent of selection and reachable by keyboard.
//
// Every task panel is mounted lazily on first visit and then KEPT MOUNTED
// (display:none) so draft-bearing forms survive a tab switch, a pin, an unpin, a
// preset change, OR a narrow/wide reflow. Each panel is rendered EXACTLY ONCE and
// NEVER reparented: the header strip and the pane row are stable sibling DOM
// subtrees, and only inline styles change between layouts. renderBookmarksLinks
// receives whether it is VISIBLE (active OR pinned), not merely selected.
//
// Sizing is governed by the canonical view-preset model (workspaceLayout.ts):
// pins decide WHICH panes are visible, the preset decides the PROPORTIONS. The
// tree takes a resizable ratio of the width; the non-tree panes share the rest in
// a grid whose columns/lead-span come from `resolveNonTreeGrid`. A lone non-tree
// pane spans the whole area; when no non-tree pane is visible the tree fills the
// width (the empty area is removed, never left as blank half). On a narrow
// viewport every visible pane stacks in one column so tabs and drafts are kept.

import { tabKeyboard } from '@/lib/tabKeyboard';
import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { WORKSPACE_TABS, type WorkspaceTabKey } from './workspaceTabs';
import {
  VIEW_PRESET_LABELS,
  WORKSPACE_VIEW_PRESETS,
  resolveNonTreeGrid,
  type WorkspaceViewPreset,
} from './workspaceLayout';
import { PushpinIcon } from './PushpinIcon';

export interface CompilerWorkspaceProps {
  /** The active tab selection (resolved/migrated by the storage layer). */
  readonly active: WorkspaceTabKey;
  /**
   * Request a tab change. A main-tab switch never discards a retained draft
   * (every visited panel stays mounted), so the caller sets the tab directly
   * here with no unsaved-work prompt.
   */
  readonly onSelect: (tab: WorkspaceTabKey) => void;
  /**
   * The ONE shared selected-file header (filename + lifecycle/signature badges
   * + rename affordance). Rendered once above both pane headers. Omitted when no
   * document is selected.
   */
  readonly header?: ReactNode;

  /** The set of pinned tabs — pinned panes stay visible while another is active. */
  readonly pinned: ReadonlySet<WorkspaceTabKey>;
  /** Toggle a tab's pin. Independent of selection; the active tab may be pinned too. */
  readonly onTogglePin: (tab: WorkspaceTabKey) => void;

  /** The chosen view preset (proportions). */
  readonly viewPreset: WorkspaceViewPreset;
  readonly onSelectPreset: (preset: WorkspaceViewPreset) => void;

  /** The tree column's fraction (0..1) of the usable width when it sits beside content. */
  readonly treeRatio: number;
  readonly onTreeRatioChange: (value: number) => void;

  /** The CTD tree (SectionTree) — rendered as the first tab's panel. */
  readonly treePanel: ReactNode;
  readonly documentsPanel: ReactNode;
  readonly reviewPanel: ReactNode;
  /**
   * Bookmarks & Links is a render-prop (not a plain node) because, like every
   * panel, it is kept mounted once visited: it must learn whether it is VISIBLE
   * (active OR pinned) so its navigation-envelope fetch refreshes on re-entry
   * and its expanded overlay folds only when actually hidden.
   */
  readonly renderBookmarksLinks: (visible: boolean) => ReactNode;
  readonly validationPanel: ReactNode;
  readonly lifecyclePanel: ReactNode;
  readonly packagePanel: ReactNode;
}

// Below this root width panes can no longer sit side by side without starving
// each other; they stack in a single column so the tabs stay put.
const NARROW_WIDTH = 640;
const MIN_TREE_WIDTH = 220;
const MIN_CONTENT_WIDTH = 360;
const DEFAULT_RATIO = 0.5;
// The width of the beside-the-tree resizer handle. The wide header row uses this
// same value as its gap so the right header column begins exactly where the
// non-tree body begins — header and body stay in the same columns (Kimi D1).
const RESIZER_WIDTH = 6;
// A floor for wide-viewport tiles so three+ panes keep usable height and scroll
// (Kimi F7) rather than collapsing to slivers.
const MIN_TILE_HEIGHT = 280;
// ONE shared comfortable pane height for the WIDE pane row. This is a DEFINITE
// height (not just a minimum) applied with `flex: 0 0 auto`, because `minHeight` +
// `overflow:auto` WITHOUT a definite height does NOT bound the panes — the tree/PDF
// would grow to its full scrollHeight. A definite height gives each stretched pane a
// real box so long content scrolls INSIDE it. ~700px at a 1000px viewport, 560px at
// ~720px; the outer page grows by header + this row rather than being pinned to the
// viewport, but a long expanded tree/PDF never grows the page indefinitely.
const WIDE_PANE_HEIGHT = 'max(560px, 70vh)';
// A sensible DEFINITE height for each stacked pane in the narrow (single-column)
// layout, so each one is bounded and scrolls internally; the parent row is auto and
// simply stacks them (no cramming every pane into one shared 560px column).
const NARROW_PANE_HEIGHT = '60vh';

/** The ratio clamp bounds for the current root width (shared by the clamp + ARIA). */
function ratioBounds(rootWidth: number): { min: number; max: number } {
  if (rootWidth <= 0) return { min: 0.15, max: 0.85 };
  const min = MIN_TREE_WIDTH / rootWidth;
  const max = 1 - MIN_CONTENT_WIDTH / rootWidth;
  if (min >= max) return { min: DEFAULT_RATIO, max: DEFAULT_RATIO };
  return { min, max };
}

/** Clamp a tree ratio so neither the tree nor the content falls below its minimum. */
function clampRatio(ratio: number, rootWidth: number): number {
  if (!Number.isFinite(ratio)) return DEFAULT_RATIO;
  const { min, max } = ratioBounds(rootWidth);
  return Math.min(max, Math.max(min, ratio));
}

const rootStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  flex: 1,
};

// Static parts of the header row; `flexWrap` and `gap` are resolved per-layout in
// the component (nowrap + resizer-matched gap when wide, wrap when narrow) so the
// right header never drops to its own full-width row below the tree (Kimi D1).
const headerRowBaseStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-end',
  borderBottom: '1px solid var(--border)',
  margin: 0,
  padding: '0 0 4px 0',
  flex: '0 0 auto',
};

const tablistStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 4,
  alignItems: 'center',
};

const tabItemStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  flexShrink: 0,
  borderTopLeftRadius: 6,
  borderTopRightRadius: 6,
  padding: '0 2px',
};

// Visible (open — active OR pinned) tab chips use the app surface (white); closed
// ones sit on the muted surface so "open vs closed" reads at a glance without
// marking every visible tab aria-selected.
// Longhand sides (no `border` shorthand) so toggling open/closed never mixes a
// shorthand with the `borderBottom` longhand — React warns on that during rerender.
const tabItemOpenStyle: CSSProperties = {
  background: 'var(--surface)',
  borderTop: '1px solid var(--border)',
  borderLeft: '1px solid var(--border)',
  borderRight: '1px solid var(--border)',
  borderBottom: 'none',
};

const tabItemClosedStyle: CSSProperties = {
  background: 'var(--surface2)',
  borderTop: '1px solid transparent',
  borderLeft: '1px solid transparent',
  borderRight: '1px solid transparent',
  borderBottom: 'none',
};

// Comfortable spacing (the workspace has room): a 13px label with 6–8px horizontal
// padding. The label never shrinks (`flexShrink: 0`) and stays on one line
// (`nowrap`); the whole strip scrolls only when a view is genuinely too narrow.
const tabBtn: CSSProperties = {
  padding: '6px 8px',
  background: 'transparent',
  border: 'none',
  borderBottom: '2px solid transparent',
  fontSize: 13,
  fontWeight: 500,
  color: 'var(--text-muted)',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  flexShrink: 0,
};

// The CTD Tree tab is the primary, always-present tab — rendered LARGER than the
// document tabs (bigger label, roomier padding, a wide comfortable chip).
const tabBtnLarge: CSSProperties = {
  fontSize: 14,
  padding: '8px 12px',
  minWidth: 100,
};

const tabActiveBtn: CSSProperties = {
  color: 'var(--accent, #4f46e5)',
  borderBottom: '2px solid var(--accent, #4f46e5)',
  fontWeight: 600,
};

const tabOpenBtn: CSSProperties = {
  color: 'var(--text)',
};

const pinBtn: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  // A comfortable ≥24×24 hit target around the 14px pushpin SVG; never shrinks.
  minWidth: 24,
  minHeight: 24,
  padding: 5,
  flexShrink: 0,
  background: 'transparent',
  border: 'none',
  lineHeight: 0,
  cursor: 'pointer',
  color: 'var(--text-muted)',
  borderRadius: 4,
};

const pinActiveBtn: CSSProperties = {
  color: 'var(--accent, #4f46e5)',
};

// Three compact LAYOUT preset buttons sit beside the CTD Tree tab (role="group",
// NOT the tablist — they never join the arrow-key cycle). Each renders a diagram
// SVG and carries a title + full aria-label + aria-pressed; `marginLeft: 'auto'`
// parks them in the tree header's spare space, near the tree/content boundary.
const layoutGroupStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  marginLeft: 'auto',
  flex: '0 0 auto',
};

const layoutBtnStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 3,
  background: 'transparent',
  border: '1px solid var(--border)',
  borderRadius: 5,
  lineHeight: 0,
  cursor: 'pointer',
  color: 'var(--text-muted)',
};

// The selected preset reads as filled/accented; `border` stays a shorthand on both
// so open/closed never mixes shorthand with a longhand (React rerender warning).
const layoutBtnSelectedStyle: CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--accent, #4f46e5)',
  color: 'var(--accent, #4f46e5)',
};

// A distinct, always-visible focus ring. Inline styles can't target :focus-visible,
// so focus is tracked in state and rendered as an accent outline.
const layoutBtnFocusStyle: CSSProperties = {
  outline: '2px solid var(--accent, #4f46e5)',
  outlineOffset: 1,
};

const panelBaseStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  minWidth: 0,
  overflow: 'auto',
};

// Kept mounted but hidden: display:none drops it from layout and the a11y tree
// while preserving every form's state.
const hiddenStyle: CSSProperties = { display: 'none' };

const resizerStyle: CSSProperties = {
  flex: `0 0 ${RESIZER_WIDTH}px`,
  alignSelf: 'stretch',
  cursor: 'col-resize',
  background: 'var(--border)',
  border: 'none',
  padding: 0,
};

// A small outlined diagram of each preset's pane geometry (not a dot/letter), so
// the three layout buttons read at a glance. aria-hidden/focusable=false — the
// button's aria-label + title carry the meaning.
function PresetDiagram({ preset }: { preset: WorkspaceViewPreset }): ReactElement {
  const strokeProps = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.2, rx: 1 };
  const panes =
    preset === 'two-equal'
      ? [{ x: 1, y: 1, width: 7.5, height: 12 }, { x: 9.5, y: 1, width: 7.5, height: 12 }]
      : preset === 'thirds'
        ? [
            { x: 1, y: 1, width: 4.67, height: 12 },
            { x: 6.67, y: 1, width: 4.66, height: 12 },
            { x: 12.33, y: 1, width: 4.67, height: 12 },
          ]
        : // half-quarters: a left half beside two quarter-width columns
          [
            { x: 1, y: 1, width: 7.5, height: 12 },
            { x: 9.5, y: 1, width: 3.25, height: 12 },
            { x: 13.75, y: 1, width: 3.25, height: 12 },
          ];
  return (
    <svg width={18} height={14} viewBox="0 0 18 14" aria-hidden="true" focusable="false">
      {panes.map((p, i) => (
        <rect key={i} x={p.x} y={p.y} width={p.width} height={p.height} {...strokeProps} />
      ))}
    </svg>
  );
}

export function CompilerWorkspace({
  active,
  onSelect,
  header,
  pinned,
  onTogglePin,
  viewPreset,
  onSelectPreset,
  treeRatio,
  onTreeRatioChange,
  treePanel,
  documentsPanel,
  reviewPanel,
  renderBookmarksLinks,
  validationPanel,
  lifecyclePanel,
  packagePanel,
}: CompilerWorkspaceProps): ReactElement {
  const isVisible = (key: WorkspaceTabKey): boolean => active === key || pinned.has(key);

  // Which layout button currently holds focus (drives the always-visible ring,
  // since inline styles cannot express :focus-visible).
  const [focusedPreset, setFocusedPreset] = useState<WorkspaceViewPreset | null>(null);

  // Keep EVERY visited panel mounted (hidden) so a tab switch, a pin, an unpin, a
  // preset change or a reflow never drops a staged reason or an unsaved input.
  const [visited, setVisited] = useState<ReadonlySet<WorkspaceTabKey>>(
    () => new Set(WORKSPACE_TABS.filter((t) => active === t.key || pinned.has(t.key)).map((t) => t.key)),
  );
  useEffect(() => {
    setVisited((prev) => {
      let next: Set<WorkspaceTabKey> | null = null;
      for (const t of WORKSPACE_TABS) {
        if ((active === t.key || pinned.has(t.key)) && !prev.has(t.key)) {
          next ??= new Set(prev);
          next.add(t.key);
        }
      }
      return next ?? prev;
    });
  }, [active, pinned]);

  const root = useRef<HTMLDivElement>(null);
  const paneRow = useRef<HTMLDivElement>(null);
  const [rootWidth, setRootWidth] = useState(1000);
  useEffect(() => {
    const element = root.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setRootWidth(entry.contentRect.width);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const narrow = rootWidth < NARROW_WIDTH;
  const treeVisible = isVisible('tree');
  const nonTreeVisible = WORKSPACE_TABS.filter((t) => t.key !== 'tree' && isVisible(t.key)).map((t) => t.key);
  const nonTreeCount = nonTreeVisible.length;
  const soleNonTree = nonTreeCount === 1 ? nonTreeVisible[0] : null;
  const firstNonTreeVisible = nonTreeVisible[0] ?? null;
  const ratio = clampRatio(treeRatio, rootWidth);
  const grid = resolveNonTreeGrid({ preset: viewPreset, treeVisible, nonTreeCount });
  // A resizer only makes sense beside the content on a wide viewport with both
  // the tree and at least one non-tree pane sharing the row.
  const showResizer = treeVisible && nonTreeCount > 0 && !narrow;

  const beginResize = (event: ReactPointerEvent<HTMLDivElement>): void => {
    event.preventDefault();
    const rect = paneRow.current?.getBoundingClientRect();
    if (!rect || rect.width <= 0) return;
    const move = (moveEvent: PointerEvent): void => {
      onTreeRatioChange(clampRatio((moveEvent.clientX - rect.left) / rect.width, rootWidth));
    };
    const end = (): void => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
  };

  const onResizeKey = (event: ReactKeyboardEvent<HTMLDivElement>): void => {
    const step = rootWidth > 0 ? 16 / rootWidth : 0.02;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      onTreeRatioChange(clampRatio(ratio - step, rootWidth));
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      onTreeRatioChange(clampRatio(ratio + step, rootWidth));
    }
  };

  // The ARIA range reports the ACTUAL clamp so assistive tech reads a true range
  // (Kimi F6), not a fixed 0–100.
  const bounds = ratioBounds(rootWidth);
  const resizeMinPct = Math.round(bounds.min * 100);
  const resizeMaxPct = Math.round(bounds.max * 100);
  const resizeNowPct = Math.min(resizeMaxPct, Math.max(resizeMinPct, Math.round(ratio * 100)));

  const panelNodeFor = (key: WorkspaceTabKey): ReactNode => {
    switch (key) {
      case 'tree':
        return treePanel;
      case 'documents':
        return documentsPanel;
      case 'review':
        return reviewPanel;
      case 'bookmarks-links':
        return renderBookmarksLinks(isVisible('bookmarks-links'));
      case 'validation':
        return validationPanel;
      case 'lifecycle':
        return lifecyclePanel;
      case 'package':
        return packagePanel;
    }
  };

  // ── Header chips ────────────────────────────────────────────────────────────
  // The focus target of the right (non-tree) tablist is the active tab when it is
  // a non-tree tab, otherwise the first non-tree tab — so roving tabindex always
  // has exactly one entry even when the TREE is the active tab.
  const rightFocusKey: WorkspaceTabKey = active === 'tree' ? 'documents' : active;

  const renderTab = (t: { key: WorkspaceTabKey; label: string }, opts?: { large?: boolean }): ReactElement => {
    const selected = active === t.key;
    const open = isVisible(t.key);
    const isPinned = pinned.has(t.key);
    const focusTarget = t.key === 'tree' ? true : t.key === rightFocusKey;
    return (
      <span
        key={t.key}
        style={{ ...tabItemStyle, ...(open ? tabItemOpenStyle : tabItemClosedStyle) }}
      >
        <button
          type="button"
          role="tab"
          id={`workspace-tab-${t.key}`}
          aria-controls={`workspace-panel-${t.key}`}
          aria-selected={selected}
          data-open={open}
          tabIndex={focusTarget ? 0 : -1}
          data-testid={`workspace-tab-${t.key}`}
          onClick={() => onSelect(t.key)}
          style={{
            ...tabBtn,
            ...(opts?.large ? tabBtnLarge : null),
            ...(open ? tabOpenBtn : null),
            ...(selected ? tabActiveBtn : null),
          }}
        >
          {t.label}
        </button>
        <button
          type="button"
          aria-pressed={isPinned}
          aria-label={isPinned ? `Unpin ${t.label}` : `Pin ${t.label}`}
          title={isPinned ? `Unpin ${t.label}` : `Pin ${t.label}`}
          data-testid={`workspace-pin-${t.key}`}
          onClick={() => onTogglePin(t.key)}
          style={{ ...pinBtn, ...(isPinned ? pinActiveBtn : null) }}
        >
          <PushpinIcon pinned={isPinned} />
        </button>
      </span>
    );
  };

  const treeTab = WORKSPACE_TABS[0];
  const otherTabs = WORKSPACE_TABS.slice(1);

  // ── Pane-row styles ─────────────────────────────────────────────────────────
  const paneRowStyle: CSSProperties = narrow
    ? {
        // Narrow: an AUTO-height row that simply stacks the panes; each pane carries
        // its own definite height (below), so nothing is crammed into one column.
        display: 'flex',
        flexDirection: 'column',
        flex: '0 0 auto',
        minHeight: 0,
        gap: 8,
      }
    : {
        // Wide: a DEFINITE row height shared by the tree and non-tree columns (they
        // stretch to it), so each pane is a real bounded box. `flex: 0 0 auto` keeps
        // it exactly this tall; the outer page grows by header + this row rather than
        // being pinned to the viewport, but long content scrolls inside the panes.
        display: 'flex',
        flexDirection: 'row',
        height: WIDE_PANE_HEIGHT,
        flex: '0 0 auto',
        minHeight: 0,
        gap: 0,
      };

  const treeColStyle: CSSProperties = !treeVisible
    ? hiddenStyle
    : narrow
      ? {
          ...panelBaseStyle,
          flex: '0 0 auto',
          height: '40vh',
          background: 'var(--surface)',
          borderBottom: '1px solid var(--border)',
          paddingTop: 12,
        }
      : nonTreeCount === 0
        // The tree is the only visible pane — let it fill the width (Kimi F1).
        ? { ...panelBaseStyle, flex: 1, background: 'var(--surface)', paddingTop: 12 }
        : {
            ...panelBaseStyle,
            flex: `0 0 ${ratio * 100}%`,
            minWidth: MIN_TREE_WIDTH,
            background: 'var(--surface)',
            paddingTop: 12,
          };

  // The non-tree area: a grid (columns from the preset) on a wide viewport, a
  // single stacked column on a narrow one. It is REMOVED when no non-tree pane is
  // visible so the tree is not left beside a blank half (Kimi F1).
  const nonTreeAreaStyle: CSSProperties =
    nonTreeCount === 0
      ? hiddenStyle
      : narrow
        ? { display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0, flex: '0 0 auto', gap: 8 }
        : {
            display: 'grid',
            gridTemplateColumns: `repeat(${grid.columns}, minmax(0, 1fr))`,
            gridAutoRows: `minmax(${MIN_TILE_HEIGHT}px, 1fr)`,
            gap: 8,
            minHeight: 0,
            minWidth: 0,
            flex: 1,
            overflow: 'auto',
          };

  const panePresentationStyle = (key: WorkspaceTabKey): CSSProperties => {
    if (!isVisible(key)) return hiddenStyle;
    if (narrow) return { ...panelBaseStyle, flex: '0 0 auto', height: NARROW_PANE_HEIGHT, background: 'var(--surface)', paddingTop: 12 };
    const span =
      soleNonTree === key
        ? '1 / -1'
        : key === firstNonTreeVisible
          ? `span ${grid.leadSpan}`
          : `span ${grid.otherSpan}`;
    return { ...panelBaseStyle, paddingTop: 12, background: 'var(--surface)', gridColumn: span };
  };

  // The TREE header COLUMN carries the ratio-based width (Kimi D1 alignment): its
  // flex basis + min-width mirror the tree BODY column, so the tree/content
  // boundary is one vertical line. It holds the single-tab tree tablist AND the
  // layout button group; `overflowX: auto` + `nowrap` mean the layout icons never
  // force the pane columns wider — they scroll within the column if ever needed.
  const treeHeaderColStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 6,
    flexWrap: 'nowrap',
    overflowX: 'auto',
    ...(!narrow && treeVisible && nonTreeCount > 0
      ? { flex: `0 0 ${ratio * 100}%`, minWidth: MIN_TREE_WIDTH }
      : { flex: '0 0 auto' }),
  };
  const treeTablistStyle: CSSProperties = { ...tablistStyle, flexWrap: 'nowrap', flex: '0 0 auto' };

  // When wide, the header row must NOT wrap the right header under the tree
  // (Kimi D1). The row gap matches the pane resizer width so the right header
  // column starts exactly where the non-tree body does; when the tree and content
  // share a wide row this keeps the two header columns aligned with the two body
  // columns. Narrow stacks (wrap allowed) — stacked bodies may carry stacked headers.
  const headerRowStyle: CSSProperties = {
    ...headerRowBaseStyle,
    flexWrap: narrow ? 'wrap' : 'nowrap',
    gap: narrow ? 8 : showResizer ? RESIZER_WIDTH : 8,
  };
  // The right strip is dedicated to the six document tabs + pins on ONE ROW: it
  // shrinks into its remaining share (`flex: 1 1 0` + `minWidth: 0`) and stays
  // `nowrap`; only when the space is genuinely insufficient does it scroll
  // horizontally (`overflowX: auto`) — never wrapping or clipping a tab/pin.
  const rightHeaderStyle: CSSProperties = {
    ...tablistStyle,
    flexWrap: 'nowrap',
    overflowX: 'auto',
    flex: '1 1 0',
    minWidth: 0,
  };

  return (
    <div ref={root} data-testid="compiler-workspace" style={rootStyle}>
      {header}

      <div style={headerRowStyle}>
        <div data-testid="workspace-tree-header" style={treeHeaderColStyle}>
          <div
            role="tablist"
            aria-label="CTD tree section"
            onKeyDown={tabKeyboard}
            style={treeTablistStyle}
          >
            {renderTab(treeTab, { large: true })}
          </div>
          <div role="group" aria-label="Workspace layout" style={layoutGroupStyle}>
            {WORKSPACE_VIEW_PRESETS.map((preset) => {
              const selected = viewPreset === preset;
              const focused = focusedPreset === preset;
              return (
                <button
                  key={preset}
                  type="button"
                  aria-pressed={selected}
                  aria-label={VIEW_PRESET_LABELS[preset]}
                  title={VIEW_PRESET_LABELS[preset]}
                  data-testid={`workspace-view-preset-${preset}`}
                  onClick={() => onSelectPreset(preset)}
                  onFocus={() => setFocusedPreset(preset)}
                  onBlur={() => setFocusedPreset((p) => (p === preset ? null : p))}
                  style={{
                    ...layoutBtnStyle,
                    ...(selected ? layoutBtnSelectedStyle : null),
                    ...(focused ? layoutBtnFocusStyle : null),
                  }}
                >
                  <PresetDiagram preset={preset} />
                </button>
              );
            })}
          </div>
        </div>
        <div
          role="tablist"
          aria-label="Document workspace sections"
          onKeyDown={tabKeyboard}
          style={rightHeaderStyle}
        >
          {otherTabs.map((t) => renderTab(t))}
        </div>
      </div>

      <div ref={paneRow} data-testid="workspace-pane-row" style={paneRowStyle}>
        {visited.has('tree') && (
          <div
            role="tabpanel"
            id="workspace-panel-tree"
            aria-labelledby="workspace-tab-tree"
            data-testid="workspace-panel-tree"
            tabIndex={active === 'tree' ? 0 : undefined}
            hidden={!treeVisible}
            style={treeColStyle}
          >
            {treePanel}
          </div>
        )}

        {showResizer && (
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize CTD tree"
            aria-valuemin={resizeMinPct}
            aria-valuemax={resizeMaxPct}
            aria-valuenow={resizeNowPct}
            tabIndex={0}
            data-testid="workspace-tree-resizer"
            onPointerDown={beginResize}
            onKeyDown={onResizeKey}
            style={resizerStyle}
          />
        )}

        <div data-testid="workspace-nontree-area" style={nonTreeAreaStyle}>
          {otherTabs.map((t) => {
            if (!visited.has(t.key)) return null;
            const visible = isVisible(t.key);
            return (
              <div
                key={t.key}
                role="tabpanel"
                id={`workspace-panel-${t.key}`}
                aria-labelledby={`workspace-tab-${t.key}`}
                data-testid={`workspace-panel-${t.key}`}
                tabIndex={active === t.key ? 0 : undefined}
                hidden={!visible}
                style={panePresentationStyle(t.key)}
              >
                {panelNodeFor(t.key)}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
